/**
 * FILE: packages/supabase/src/services/communityLinks.ts
 * PURPOSE: Community links CRUD + outreach helpers (Hub support/placements).
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 */

import {
  attemptsMatch,
  canonicalCourseForStorage,
  coursesMatch,
  isFutureOrCurrentAttempt,
  isValidStorageAttempt,
} from '@fintree/utils'
import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import { ServiceError } from '../internal/errors'
import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

type CommunityLinkRow = Database['public']['Tables']['community_links']['Row']
type CommunityLinkInsert = Database['public']['Tables']['community_links']['Insert']

export type CommunityLinkType = 'placement' | 'attempt'

export interface CommunityLinkDTO {
  id: string
  type: CommunityLinkType
  exp_band: string | null
  course: string | null
  attempt: string | null
  link: string
  label: string | null
  is_active: boolean | null
  created_at: string | null
}

export interface CreateAttemptCommunityLinkPayload {
  course: string
  attempt: string
  link: string
  label?: string
  is_active: boolean
}

export interface UpdateAttemptCommunityLinkPayload extends CreateAttemptCommunityLinkPayload {
  id: string
}

export interface CreatePlacementCommunityLinkPayload {
  exp_band: string
  link: string
  label?: string
  is_active: boolean
}

export interface UpdatePlacementCommunityLinkPayload extends CreatePlacementCommunityLinkPayload {
  id: string
}

export interface MissingAttemptCommunityLink {
  course: string
  attempt: string
  count: number
}

export interface SendLinkStudentRow {
  phone: string
  name: string | null
  course: string | null
  attempt: string | null
  status: string
}

function toDto(row: CommunityLinkRow): CommunityLinkDTO {
  return {
    id: row.id,
    type: row.type as CommunityLinkType,
    exp_band: row.exp_band,
    course: row.course,
    attempt: row.attempt,
    link: row.link,
    label: row.label,
    is_active: row.is_active,
    created_at: row.created_at,
  }
}

export async function fetchCommunityLinks(type: CommunityLinkType, client: DbClient = defaultDbClient): Promise<CommunityLinkDTO[]> {
  const { data, error } = await client
    .from('community_links')
    .select('*')
    .eq('type', type)
    .order('created_at', { ascending: false })

  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINKS_FETCH', error)
  return (data ?? []).map(toDto)
}

export async function fetchActiveCommunityLinks(client: DbClient = defaultDbClient): Promise<CommunityLinkDTO[]> {
  const { data, error } = await client
    .from('community_links')
    .select('*')
    .eq('is_active', true)
    .order('type', { ascending: true })

  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINKS_ACTIVE', error)
  return (data ?? []).map(toDto)
}

/** Hub display form: CFA_L2 → CFA L2 */
export function canonicalCommunityCourse(course: string): string {
  return course.trim().replace(/_/g, ' ')
}

/** Hub display form: Aug_27 → Aug 2027 */
export function canonicalCommunityAttempt(attempt: string): string {
  const trimmed = attempt.trim()
  if (trimmed.includes('_')) {
    const [month, year] = trimmed.split('_')
    if (!month || !year) return trimmed
    const fullYear = year.length === 2 ? `20${year}` : year
    return `${month} ${fullYear}`
  }
  if (trimmed.includes(' ')) {
    const [month, year] = trimmed.split(' ')
    if (year?.length === 2) return `${month} 20${year}`
  }
  return trimmed
}

export async function createAttemptCommunityLink(
  payload: CreateAttemptCommunityLinkPayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const course = canonicalCommunityCourse(payload.course)
  const attempt = canonicalCommunityAttempt(payload.attempt)
  const label = payload.label?.trim() || `${course} — ${attempt}`
  const row: CommunityLinkInsert = {
    type: 'attempt',
    course,
    attempt,
    link: payload.link.trim(),
    label,
    is_active: payload.is_active,
  }

  const { data, error } = await client.from('community_links').insert(row).select('id').single()
  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINK_CREATE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_COMMUNITY_LINK',
      entityType: 'community_link',
      entityId: data.id,
      newValues: row,
    }), client
  )
}

export async function updateAttemptCommunityLink(
  payload: UpdateAttemptCommunityLinkPayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const course = canonicalCommunityCourse(payload.course)
  const attempt = canonicalCommunityAttempt(payload.attempt)
  const label = payload.label?.trim() || `${course} — ${attempt}`
  const newValues = {
    course,
    attempt,
    link: payload.link.trim(),
    label,
    is_active: payload.is_active,
  }
  const { error } = await client.from('community_links').update(newValues).eq('id', payload.id)

  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINK_UPDATE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_COMMUNITY_LINK',
      entityType: 'community_link',
      entityId: payload.id,
      newValues,
    }), client
  )
}

export async function createPlacementCommunityLink(
  payload: CreatePlacementCommunityLinkPayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const label = payload.label?.trim() || `Placements — ${payload.exp_band} years`
  const row: CommunityLinkInsert = {
    type: 'placement',
    exp_band: payload.exp_band,
    link: payload.link.trim(),
    label,
    is_active: payload.is_active,
  }

  const { data, error } = await client.from('community_links').insert(row).select('id').single()
  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINK_CREATE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_COMMUNITY_LINK',
      entityType: 'community_link',
      entityId: data.id,
      newValues: row,
    }), client
  )
}

export async function updatePlacementCommunityLink(
  payload: UpdatePlacementCommunityLinkPayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<void> {
  const label = payload.label?.trim() || `Placements — ${payload.exp_band} years`
  const newValues = {
    exp_band: payload.exp_band,
    link: payload.link.trim(),
    label,
    is_active: payload.is_active,
  }
  const { error } = await client.from('community_links').update(newValues).eq('id', payload.id)

  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINK_UPDATE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_COMMUNITY_LINK',
      entityType: 'community_link',
      entityId: payload.id,
      newValues,
    }), client
  )
}

export async function deactivateCommunityLink(id: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client.from('community_links').update({ is_active: false }).eq('id', id)
  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINK_DEACTIVATE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'DEACTIVATE_COMMUNITY_LINK',
      entityType: 'community_link',
      entityId: id,
      newValues: { is_active: false },
    }), client
  )
}

/** Permanently remove a link. Clears outreach FK references first. */
export async function deleteCommunityLink(id: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error: outreachError } = await client
    .from('community_link_outreach')
    .update({ community_link_id: null })
    .eq('community_link_id', id)

  if (outreachError) {
    throw new ServiceError(outreachError.message, 'COMMUNITY_LINK_OUTREACH_CLEAR', outreachError)
  }

  const { error } = await client.from('community_links').delete().eq('id', id)
  if (error) throw new ServiceError(error.message, 'COMMUNITY_LINK_DELETE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'DELETE_COMMUNITY_LINK',
      entityType: 'community_link',
      entityId: id,
    }), client
  )
}

/** Students + catalog-only registry rows missing active attempt community links. */
export async function fetchMissingAttemptCommunityLinks(
  activeLinks: CommunityLinkDTO[], client: DbClient = defaultDbClient,
): Promise<MissingAttemptCommunityLink[]> {
  const [{ data: students, error }, { data: catalogAttempts, error: catalogError }] = await Promise.all([
    client
      .from('students')
      .select('course, attempt')
      .is('deleted_at', null)
      .not('course', 'is', null)
      .not('attempt', 'is', null),
    client
      .from('course_attempts')
      .select('course, attempt, display_attempt, student_count, sources')
      .eq('is_active', true)
      .eq('sources', ['catalog'])
      .eq('student_count', 0),
  ])

  if (error) throw new ServiceError(error.message, 'MISSING_LINKS_STUDENTS', error)
  if (catalogError) throw new ServiceError(catalogError.message, 'MISSING_LINKS_REGISTRY', catalogError)

  const comboMap = new Map<string, MissingAttemptCommunityLink>()
  for (const s of students ?? []) {
    if (!isFutureOrCurrentAttempt(s.attempt)) continue
    const key = `${s.course}||${s.attempt}`
    const existing = comboMap.get(key)
    if (existing) {
      existing.count++
    } else {
      comboMap.set(key, { course: s.course!, attempt: s.attempt!, count: 1 })
    }
  }

  for (const row of catalogAttempts ?? []) {
    if (!isValidStorageAttempt(row.attempt)) continue
    const displayAttempt = row.display_attempt ?? row.attempt
    if (!isFutureOrCurrentAttempt(displayAttempt) && !isFutureOrCurrentAttempt(row.attempt)) continue
    const key = `${row.course}||${row.attempt}`
    if (!comboMap.has(key)) {
      comboMap.set(key, {
        course: row.course,
        attempt: displayAttempt,
        count: 0,
      })
    }
  }

  const missing: MissingAttemptCommunityLink[] = []
  for (const combo of comboMap.values()) {
    const hasLink = activeLinks.some(
      (l) =>
        l.is_active &&
        coursesMatch(l.course, combo.course) &&
        attemptsMatch(l.attempt, combo.attempt),
    )
    if (!hasLink) missing.push(combo)
  }

  return missing.sort((a, b) => b.count - a.count)
}

export async function searchStudentsForLinkOutreach(
  search: string,
  courseFilter: string, client: DbClient = defaultDbClient,
): Promise<SendLinkStudentRow[]> {
  let q = client
    .from('students')
    .select('phone, name, course, attempt, enrollments(status)')
    .is('deleted_at', null)
    .limit(200)

  const trimmedSearch = search.trim()
  if (trimmedSearch) {
    q = q.or(`name.ilike.%${trimmedSearch}%,phone.ilike.%${trimmedSearch}%`)
  }
  if (courseFilter !== 'All Courses') {
    const canonical = canonicalCourseForStorage(courseFilter)
    const courseVariants = [...new Set([canonical, courseFilter].filter(Boolean))]
    if (canonical.includes('_')) courseVariants.push(canonical.replace(/_/g, ' '))
    if (courseFilter.includes(' ')) courseVariants.push(courseFilter.replace(/ /g, '_'))
    q = q.in('course', [...new Set(courseVariants)])
  }

  const { data, error } = await q
  if (error) throw new ServiceError(error.message, 'SEND_LINK_STUDENTS', error)

  return (data ?? []).map((row) => {
    const enrollments = row.enrollments as Array<{ status: string }> | null
    const activeEnrollment = enrollments?.find((e) => e.status === 'active')
    return {
      phone: row.phone,
      name: row.name,
      course: row.course,
      attempt: row.attempt,
      status: activeEnrollment ? 'active' : (enrollments?.[0]?.status ?? 'none'),
    }
  })
}

